import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { getApiUrl, apiRequest } from '../config/api';
import InstagramCommentsManager from './InstagramCommentsManager';

/**
 * Panel "Instagram" para un bot. Sustituye al render directo del antiguo
 * "Comentarios IG" e integra conectar / gestionar comentarios / desconectar:
 *
 *  - GET  /auth/instagram/status?botId=...   → estado actual de la conexión.
 *  - POST /auth/instagram/provision?botId=...→ inicia OAuth (idempotente).
 *  - DELETE /auth/instagram/disconnect?botChannelId=...→ revoca y borra token.
 *
 * Renderiza tres pantallas según el estado:
 *   • CARGANDO       — spinner mientras se obtiene el estado.
 *   • DESCONECTADO   — botón "Conectar con Instagram" con tema gradient IG.
 *   • CONECTADO      — banner con @username + desconectar + comments manager.
 */
function InstagramSettingsPanel({ botId }) {
  const [status, setStatus] = useState(null); // null = cargando
  const [statusData, setStatusData] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  // El alta de Instagram (OAuth) solo se ofrece en escritorio: en móvil el
  // diálogo de Meta / los Universal Links no completan el flujo. El canal
  // ya conectado sí se puede ver y usar desde el teléfono.
  const isMobileUA = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /android|iphone|ipad|ipod/i.test(navigator.userAgent || '');
  }, []);

  const loadStatus = useCallback(async () => {
    setStatusError(null);
    try {
      const res = await apiRequest(getApiUrl(`/auth/instagram/status?botId=${botId}`));
      if (!res.ok) {
        setStatusError(`No se pudo obtener el estado (HTTP ${res.status})`);
        setStatus('ERROR');
        return;
      }
      const data = await res.json();
      setStatusData(data);
      setStatus(data.status || 'NOT_PROVISIONED');
    } catch (e) {
      setStatusError('Error de red comprobando el estado de Instagram');
      setStatus('ERROR');
    }
  }, [botId]);

  useEffect(() => {
    setStatus(null);
    setStatusData(null);
    loadStatus();
  }, [loadStatus]);

  const fetchAuthorizationUrl = async () => {
    const response = await apiRequest(
      getApiUrl(`/auth/instagram/provision?botId=${botId}`),
      { method: 'POST' }
    );
    if (!response.ok) {
      let msg = 'Error iniciando conexión con Instagram';
      try {
        const err = await response.json();
        msg = err.message || msg;
      } catch (_) {}
      throw new Error(msg);
    }
    const data = await response.json();
    if (!data.authorizationUrl) {
      throw new Error(data.message || 'No se pudo iniciar la conexión');
    }
    return data.authorizationUrl;
  };

  const handleConnect = async () => {
    setActionError(null);
    setConnecting(true);
    try {
      const url = await fetchAuthorizationUrl();
      window.location.assign(url);
    } catch (error) {
      console.error('Error connecting Instagram:', error);
      setActionError(error.message || 'Error de conexión');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!statusData?.botChannelId) return;
    setActionError(null);
    setDisconnecting(true);
    try {
      const response = await apiRequest(
        getApiUrl(`/auth/instagram/disconnect?botChannelId=${statusData.botChannelId}`),
        { method: 'DELETE' }
      );
      if (response.ok) {
        setConfirmDisconnect(false);
        await loadStatus();
      } else {
        let msg = 'Error desconectando Instagram';
        try {
          const err = await response.json();
          msg = err.message || msg;
        } catch (_) {}
        setActionError(msg);
      }
    } catch (error) {
      console.error('Error disconnecting Instagram:', error);
      setActionError('Error de conexión');
    } finally {
      setDisconnecting(false);
    }
  };

  const isConnected = status === 'CONNECTED';
  const needsReauth = status === 'NEEDS_REAUTH';

  return (
    <div className="ig-settings-panel">
      {/* Cargando */}
      {status === null && (
        <div className="ig-settings-loading">
          <div className="ig-settings-spinner" />
          <span>Cargando estado de Instagram…</span>
        </div>
      )}

      {/* Error de estado */}
      {status === 'ERROR' && (
        <div className="ig-settings-error">
          <p>{statusError || 'No se pudo obtener el estado de Instagram.'}</p>
          <button type="button" className="ig-settings-retry-btn" onClick={loadStatus}>
            Reintentar
          </button>
        </div>
      )}

      {/* Conectado: barra superior + comments manager */}
      {isConnected && (
        <>
          <div className="ig-settings-connected-bar">
            <div className="ig-settings-connected-info">
              <div className="ig-settings-avatar">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="17" cy="7" r="1.2" fill="currentColor"/>
                </svg>
              </div>
              <div>
                <div className="ig-settings-connected-username">@{statusData?.username || 'desconocido'}</div>
                <div className="ig-settings-connected-status">
                  <span className="ig-settings-status-dot" /> Cuenta conectada
                </div>
              </div>
            </div>
            <button
              type="button"
              className="ig-settings-disconnect-btn"
              onClick={() => setConfirmDisconnect(true)}
              disabled={disconnecting}
            >
              {disconnecting ? 'Desconectando…' : 'Desconectar'}
            </button>
          </div>

          {actionError && (
            <div className="ig-settings-action-error">{actionError}</div>
          )}

          <div className="ig-settings-comments-wrapper">
            <InstagramCommentsManager botId={botId} />
          </div>
        </>
      )}

      {/* Desconectado / pendiente OAuth / requiere re-auth → pantalla de conectar */}
      {(status === 'NOT_PROVISIONED' || status === 'PENDING_OAUTH' || needsReauth) && (
        <div className="ig-settings-connect-screen">
          <div className="ig-settings-connect-card">
            <div className="ig-settings-logo">
              <svg viewBox="0 0 24 24" width="44" height="44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="5" stroke="white" strokeWidth="2"/>
                <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="2"/>
                <circle cx="17" cy="7" r="1.4" fill="white"/>
              </svg>
            </div>
            <h3 className="ig-settings-connect-title">
              {needsReauth ? 'Reconecta tu cuenta de Instagram' : 'Conecta tu cuenta de Instagram'}
            </h3>
            <p className="ig-settings-connect-desc">
              {needsReauth
                ? `El acceso a @${statusData?.username || 'tu cuenta'} ha caducado. Vuelve a autorizar para reanudar la gestión de comentarios y mensajes.`
                : 'Vincula tu cuenta profesional o de creador de Instagram para recibir mensajes directos, gestionar comentarios y automatizar respuestas.'}
            </p>

            {actionError && (
              <div className="ig-settings-action-error">{actionError}</div>
            )}

            {isMobileUA ? (
              <div className="ig-settings-warning">
                La conexión del canal solo se puede hacer desde un ordenador.
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="ig-connect-btn"
                  onClick={handleConnect}
                  disabled={connecting}
                >
                  {connecting ? (
                    <>
                      <span className="ig-connect-btn-spinner" />
                      <span>Conectando…</span>
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <rect x="3" y="3" width="18" height="18" rx="5" stroke="white" strokeWidth="2"/>
                        <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="2"/>
                        <circle cx="17" cy="7" r="1.4" fill="white"/>
                      </svg>
                      <span>{needsReauth ? 'Reconectar con Instagram' : 'Conectar con Instagram'}</span>
                    </>
                  )}
                </button>

                <p className="ig-settings-connect-note">
                  Te llevaremos al inicio de sesión oficial de Instagram. No
                  guardamos tu contraseña: usamos OAuth y un token de acceso que
                  puedes revocar en cualquier momento.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmación para desconectar */}
      {confirmDisconnect && createPortal(
        <div className="modal-overlay" onClick={() => !disconnecting && setConfirmDisconnect(false)}>
          <div
            className="modal-content confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ig-disconnect-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="ig-disconnect-title">Desconectar Instagram</h3>
              <button
                className="modal-close"
                onClick={() => !disconnecting && setConfirmDisconnect(false)}
                disabled={disconnecting}
              >×</button>
            </div>
            <div className="confirm-modal-body">
              <p>
                ¿Seguro que quieres desconectar la cuenta
                {statusData?.username ? <> <strong>@{statusData.username}</strong></> : null}?
              </p>
              <p className="confirm-modal-note">
                Dejaremos de recibir webhooks de mensajes y comentarios. Podrás
                volver a conectar la cuenta cuando quieras desde esta misma pantalla.
              </p>
            </div>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="admin-btn"
                onClick={() => setConfirmDisconnect(false)}
                disabled={disconnecting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="admin-btn danger"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? 'Desconectando…' : 'Sí, desconectar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default InstagramSettingsPanel;
